// Profiler module — TypeScript type mirrors of backend Pydantic models

export type SeedType =
  | 'NAME' | 'EMAIL' | 'PHONE' | 'PAN' | 'COMPANY' | 'DOMAIN' | 'IP'
  | 'USERNAME' | 'ADDRESS' | 'VOTER_ID' | 'DL' | 'DOB' | 'PASSPORT' | 'RELATION';

export type RiskTier = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type CaseStatus = 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED';
export type ModuleStatus = 'COMPLETE' | 'RUNNING' | 'QUEUED' | 'FAILED' | 'DEGRADED';
export type MatchType = 'EXACT' | 'CLOSE' | 'WEAK';
export type AdverseCategory =
  | 'CRIMINAL' | 'FRAUD' | 'REGULATORY_ACTION' | 'INSOLVENCY'
  | 'ADVERSE_OTHER' | 'NOT_ADVERSE';
export type CaseType = 'CDD' | 'EDD';

// ── Identifier Cluster ───────────────────────────────────────────────────────

export interface IdentifierVariant {
  value: string;
  type: SeedType;
  confidence: number;
  source: string;
  found_at?: string;
  breach_databases?: string[];  // actual breach DB names e.g. ["LinkedIn_2021"]
  pivot_via?: string;           // R2: identifier that triggered this pivot lookup
}

export interface IdentifierCluster {
  canonical: string;
  variants: IdentifierVariant[];
  cluster_confidence: number;
  all_names?: string[];
  discarded_variants?: string[];
}

// ── Sanctions ─────────────────────────────────────────────────────────────────

export interface SanctionsHit {
  entity_id: string;
  caption: string;
  score: number;
  datasets: string[];
  match_type: MatchType;
  properties?: Record<string, unknown>;
}

export interface SanctionsResult {
  hits: SanctionsHit[];
  highest_score: number;
  list_coverage: string[];
  degraded: boolean;
}

// ── PEP / UBO ─────────────────────────────────────────────────────────────────

export interface PEPHit {
  name: string;
  position?: string;
  country?: string;
  source?: string;
  score?: number;
}

export interface UBOHit {
  name: string;
  relationship?: string;
  ownership_pct?: string;
  source?: string;
}

export interface PEPResult {
  pep_hits: PEPHit[];
  degraded?: boolean;
}

export interface PEPModule {
  pep_result?: PEPResult;
  ubo_hits?: UBOHit[];
  degraded?: boolean;
}

// ── Adverse Media ─────────────────────────────────────────────────────────────

export interface AdverseArticle {
  title: string;
  snippet: string;
  url: string;
  source: string;
  published_date: string;
  category: AdverseCategory;
  confidence: number;
  summary?: string;
  fraud_amount?: string;
}

export interface AdverseMediaResult {
  articles: AdverseArticle[];
  degraded?: boolean;
}

// ── Enforcement ───────────────────────────────────────────────────────────────

export interface EnforcementHit {
  authority?: string;
  action_type?: string;
  date?: string;
  summary?: string;
  url?: string;
}

// ── Risk Scoring ──────────────────────────────────────────────────────────────

export interface RiskFactor {
  code: string;
  description?: string;
  category?: string;
  weight?: number;
  score?: number;
  is_hard_rule?: boolean;
}

export interface RiskScore {
  tier: RiskTier;
  overall_score: number;
  sanctions_score: number;
  pep_score: number;
  adverse_media_score: number;
  enforcement_score: number;
  ato_score: number;
  confidence: number;
  factors?: RiskFactor[];
  degraded_modules?: string[];
}

// ── ATO / Breach ──────────────────────────────────────────────────────────────

export interface ATOSignal {
  breach_count?: number;
  stealer_log_count?: number;
  email_exposure_count?: number;
  corporate_email_hits?: number;
  risk_level?: { value: 'HIGH' | 'MEDIUM' | 'LOW' };
  most_recent_breach?: string;
  exposed_data_types?: string[];
  breach_sources?: string[];
}

// ── Wealth ────────────────────────────────────────────────────────────────────

export interface VPARecord {
  handle: string;
  verified: boolean;
}

export interface PropertyRecord {
  address?: string;
  state?: string;
  survey_no?: string;
  consideration_value_lakhs?: number;
}

export interface VehicleRecord {
  rc_number?: string;
  maker?: string;
  model?: string;
  year?: number;
  ex_showroom_value_approx?: number;
}

export interface WealthProfile {
  upi_handles?: VPARecord[];
  properties?: PropertyRecord[];
  vehicle_assets?: { vehicle_count: number; total_vehicle_value_approx_lakhs: number };
  total_property_value_approx_lakhs?: number;
  total_asset_value_approx_lakhs?: number;
  credit?: { score?: number; bureau?: string };
  crypto_registered?: boolean;
  degraded_sources?: string[];
}

// ── Social / Digital ──────────────────────────────────────────────────────────

export interface SocialAccount {
  platform: string;
  email_used?: string;
  confirmed?: boolean;
  confidence?: number;
  username?: string;
  url?: string;
}

// ── Legal ─────────────────────────────────────────────────────────────────────

export interface CourtCase {
  cnr?: string;
  court?: string;
  case_type?: string;
  parties?: string;
  filed_date?: string;
  status?: string;
  next_hearing?: string;
}

// ── Corporate ─────────────────────────────────────────────────────────────────

export interface CompanyRecord {
  cin: string;
  name: string;
  status: string;
  registration_date?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  nature_of_business?: string;
  authorized_capital?: string;
  paid_up_capital?: string;
  co_directors: string[];
}

export interface MCA21Result {
  companies_as_director: CompanyRecord[];
  active_directorships: number;
  struck_off_connections: number;
  high_risk_flag: boolean;
}

export interface SEBIResult {
  is_registered: boolean;
  registration_no?: string;
  category?: string;
  is_debarred: boolean;
  debarment_orders?: Array<Record<string, unknown>>;
}

export interface ICAIResult {
  is_registered: boolean;
  membership_no?: string;
  status?: string;
}

export interface IndiaCorporateResult {
  entity_id?: string;
  mca21?: MCA21Result;
  sebi?: SEBIResult;
  icai?: ICAIResult;
  degraded_sources?: string[];
}

// Legacy flat type kept for any existing usage
export interface CorporateRecord {
  name: string;
  type?: string;
  role?: string;
  status?: string;
  cin?: string;
  risk_flags?: string[];
}

// ── India Identity ────────────────────────────────────────────────────────────

export interface IdentityResult {
  pan_verified?: boolean;
  voter_verified?: boolean;
  dl_verified?: boolean;
  name_on_pan?: string;
  dob?: string;
  nationality?: string;
  voter_id?: string;
  dl_number?: string;
  vehicles?: VehicleRecord[];
}

// ── Attack Surface ────────────────────────────────────────────────────────────

export interface SubdomainEntry {
  subdomain: string;
  first_seen?: string;
  issuer?: string;
  is_sensitive: boolean;
}

export interface EmailSecurityPosture {
  spf_present: boolean;
  spf_strict: boolean;
  dmarc_present: boolean;
  dmarc_policy?: string;   // none | quarantine | reject
  dkim_selectors_found: string[];
  grade: string;            // A | B | C | D | F
}

export interface CompanyAttackSurface {
  domain: string;
  subdomains: SubdomainEntry[];
  subdomain_count: number;
  sensitive_subdomain_count: number;
  email_posture?: EmailSecurityPosture;
  checked_at?: string;
}

// ── Commerce Footprint ────────────────────────────────────────────────────────

export interface CommerceFootprint {
  upi_handles_enumerated?: number;
  upi_handles_verified?: number;
  upi_customer_name?: string;
  primary_upi_app?: string;
  active_commerce_signals?: number;
  lifestyle_tier?: 'LOW' | 'MEDIUM' | 'HIGH';
  degraded?: boolean;
}

// ── LinkedIn Profile ──────────────────────────────────────────────────────────

export interface LinkedInPosition {
  title?: string;
  company?: string;
  start_year?: number;
  end_year?: number;
  is_current?: boolean;
}

export interface LinkedInProfile {
  username?: string;
  full_name?: string;
  headline?: string;
  location?: string;
  follower_count?: number;
  positions?: LinkedInPosition[];
  degraded?: boolean;
  matched_by?: string;
}

// ── Pipeline Result ───────────────────────────────────────────────────────────

export interface PipelineResult {
  cluster: IdentifierCluster;
  identity?: IdentityResult;
  sanctions?: SanctionsResult;
  pep?: PEPModule;
  ato?: ATOSignal;
  wealth?: WealthProfile;
  social?: { accounts: SocialAccount[] };
  corporate?: IndiaCorporateResult;
  court?: { cases: CourtCase[] };
  adverse_media?: AdverseMediaResult;
  company_exposure?: CompanyAttackSurface;
  enforcement_hits?: EnforcementHit[];
  commerce?: CommerceFootprint;
  linkedin?: LinkedInProfile;
}

// ── Check Results / Module Status ─────────────────────────────────────────────

export interface CheckResult {
  module: string;
  source?: string;
  result_summary?: string;
  hit_found?: boolean;
  confidence?: number;
  checked_at?: string;
  status?: ModuleStatus;
  detail?: Record<string, unknown>;
}

// ── Case Reports ──────────────────────────────────────────────────────────────

export interface CDDReport {
  case_id: string;
  case_reference: string;
  risk_tier: RiskTier;
  overall_score?: number;
  sanctions_hit?: boolean;
  pep_hit?: boolean;
  identity_verified?: boolean;
  corporate_flags?: string[];
  degraded_modules?: string[];
  completed_at?: string;
  report_url?: string;
  check_results?: CheckResult[];
  identifier_cluster?: IdentifierCluster;
  risk_score?: RiskScore;
  status: CaseStatus;
  seed?: string;
  seed_type?: SeedType;
  client_ref?: string;
  created_at?: string;
  case_type?: CaseType;
  pipeline_result?: PipelineResult;
}

export interface EDDReport extends CDDReport {
  adverse_media?: AdverseArticle[];
  court_cases?: CourtCase[];
  social_accounts?: SocialAccount[];
  wealth_profile?: WealthProfile;
}

// ── Input types ───────────────────────────────────────────────────────────────

export interface CDDInput {
  seed: string;
  seed_type?: SeedType;
  client_ref?: string;
  modules?: string[];
}

export interface EDDInput {
  seed: string;
  seed_type?: SeedType;
  client_ref?: string;
  webhook_url?: string;
}

export interface CaseListParams {
  tier?: string;
  type?: CaseType;
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CaseListResponse {
  cases: CDDReport[];
  total: number;
}

// ── Design constants ──────────────────────────────────────────────────────────

export const TIER_COLOR: Record<RiskTier, string> = {
  CRITICAL: '#FF1F1F',
  HIGH:     '#FF6B1A',
  MEDIUM:   '#F0B429',
  LOW:      '#00C853',
};

export const TIER_BADGE_CLASS: Record<RiskTier, string> = {
  CRITICAL: 'bg-red-500/15 border border-red-500/30 text-red-400',
  HIGH:     'bg-orange-500/15 border border-orange-500/30 text-orange-400',
  MEDIUM:   'bg-amber-500/15 border border-amber-500/30 text-amber-400',
  LOW:      'bg-green-500/15 border border-green-500/30 text-green-400',
};

export const ADVERSE_COLOR: Record<AdverseCategory, string> = {
  CRIMINAL:          '#FF1F1F',
  FRAUD:             '#FF6B1A',
  REGULATORY_ACTION: '#F0B429',
  INSOLVENCY:        '#C026D3',
  ADVERSE_OTHER:     '#FF6B1A',
  NOT_ADVERSE:       '#5A7A9A',
};

export const SEED_LABELS: Record<string, string> = {
  NAME: 'Name', EMAIL: 'Email', PHONE: 'Phone', PAN: 'PAN',
  COMPANY: 'Company', DOMAIN: 'Domain', IP: 'IP Address',
  USERNAME: 'Username', ADDRESS: 'Address', VOTER_ID: 'Voter ID',
  DL: "Driver's Licence", DOB: 'Date of Birth',
  PASSPORT: 'Passport', RELATION: 'Relation',
};

export const MODULE_LABELS: Record<string, string> = {
  sanctions:          'Sanctions',
  pep:                'PEP / UBO',
  adverse_media:      'Adverse Media',
  enforcement:        'Enforcement',
  ato:                'ATO / Breach',
  social:             'Social Discovery',
  corporate:          'India Corporate',
  identity:           'India Identity',
  wealth:             'Wealth Signals',
  court:              'eCourts / Legal',
  breach_enrichment:  'Breach + SERP Intel',
};
